import { fetchGames } from './utils/api.js';

export default class RemoteGameStore {
  constructor(options = {}) {
    this.state = {
      category: options.category || 'all',
      sort: options.sort || 'latest',
      search: options.search || '',
      page: options.page || 1,
      perPage: options.perPage || 24,
    };
    this.allowEmptySearch = options.allowEmptySearch !== false;
    this.items = [];
    this.total = 0;
    this.loading = false;
    this.error = null;
    this.listeners = new Set();
    this.requestId = 0;
  }

  subscribe(fn) {
    this.listeners.add(fn);
    fn(this.getSnapshot());
    return () => this.listeners.delete(fn);
  }

  emit() {
    const snapshot = this.getSnapshot();
    this.listeners.forEach(listener => listener(snapshot));
  }

  getSnapshot() {
    return {
      state: { ...this.state },
      total: this.total,
      visible: [...this.items],
      hasMore: this.items.length < this.total,
      loading: this.loading,
      error: this.error,
    };
  }

  setState(patch) {
    this.state = { ...this.state, ...patch };
  }

  async init() {
    await this.fetchPage({ reset: true });
  }

  async setCategory(category) {
    this.setState({ category, page: 1 });
    await this.fetchPage({ reset: true });
  }

  async setSearch(search) {
    this.setState({ search, page: 1 });
    await this.fetchPage({ reset: true });
  }

  async setSort(sort) {
    this.setState({ sort, page: 1 });
    await this.fetchPage({ reset: true });
  }

  async loadMore() {
    if (this.loading) return;
    this.setState({ page: this.state.page + 1 });
    await this.fetchPage({ append: true });
  }

  async fetchPage({ reset = false, append = false } = {}) {
    const requestId = ++this.requestId;
    this.loading = true;
    this.error = null;
    this.emit();

    const { category, search, sort, page, perPage } = this.state;
    const hasSearch = Boolean(search && search.trim());
    if (!this.allowEmptySearch && !hasSearch) {
      this.items = [];
      this.total = 0;
      this.loading = false;
      this.emit();
      return;
    }

    try {
      const offset = (page - 1) * perPage;
      const query = {
        q: hasSearch ? search.trim() : undefined,
        category: category && category !== 'all' ? category : undefined,
        sort,
        limit: perPage,
        offset,
      };
      const data = await fetchGames(query);
      if (requestId !== this.requestId) return;

      const items = Array.isArray(data.items) ? data.items : [];
      this.items = append ? [...this.items, ...items] : items;
      const totalValue = Number(data.total);
      this.total = Number.isFinite(totalValue)
        ? totalValue
        : append
        ? this.items.length
        : items.length;
    } catch (err) {
      if (requestId !== this.requestId) return;
      this.error = err;
      if (reset) {
        this.items = [];
        this.total = 0;
      }
    } finally {
      if (requestId !== this.requestId) return;
      this.loading = false;
      this.emit();
    }
  }
}
